import React, { useEffect, useMemo, useState } from "react";
import "./UsersManager.css";

const API_URL = "https://cms.amservices.eu/api/ams_users_manager.php";
const MASTER_LOGINS = ["aurelien", "aymeric"];

const EMPTY_FORM = {
  id: null, nom: "", username: "", password: "", role: "tech",
  affectation_depannage: true, affectation_bureau: true,
};
const ROLES = [
  { value: "tech", label: "Technicien", icon: "🟢", desc: "Accès aux outils attribués." },
  { value: "bureau", label: "Bureau", icon: "🔵", desc: "Accès bureau et outils opérationnels." },
  { value: "admin", label: "Administrateur", icon: "🔴", desc: "Accès complet aux outils." },
];
const ROLE_MAP = Object.fromEntries(ROLES.map((item) => [item.value, item]));

function token() {
  const user = JSON.parse(localStorage.getItem("am_user") || "null");
  return localStorage.getItem("am_token") || user?.token || "";
}

function isMaster(user) {
  return MASTER_LOGINS.includes(String(user?.username || user?.login || "").toLowerCase());
}

function formatDate(value) {
  if (!value) return "Jamais";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("fr-FR");
}

export default function AmsUsersManager({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");

  const editingUser = users.find((item) => Number(item.id) === Number(form.id));
  const editingMaster = isMaster(editingUser);
  const selectedRole = ROLE_MAP[form.role] || ROLES[0];

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) =>
      [user.id, user.nom, user.username, user.role].some((value) =>
        String(value || "").toLowerCase().includes(query)
      )
    );
  }, [users, search]);

  const apiRequest = async (payload = null) => {
    const authToken = token();
    const response = await fetch(API_URL, {
      method: payload ? "POST" : "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-Auth-Token": authToken,
      },
      ...(payload ? { body: JSON.stringify(payload) } : {}),
    });
    const text = await response.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error("Réponse invalide du serveur.");
    }
    if (!response.ok || !data.success) throw new Error(data.message || `Erreur HTTP ${response.status}`);
    return data;
  };

  const loadUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiRequest();
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (err) {
      setError(err.message || "Impossible de charger les utilisateurs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (payload) => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const data = await apiRequest(payload);
      setUsers(Array.isArray(data.users) ? data.users : []);
      setMessage(data.message || "Opération terminée.");
      return true;
    } catch (err) {
      setError(err.message || "L’opération a échoué.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setForm(EMPTY_FORM);
    setError("");
  };

  const edit = (user) => {
    setForm({
      id: Number(user.id), nom: user.nom || "", username: user.username || "",
      password: "", role: user.role || "tech",
      affectation_depannage: Boolean(Number(user.affectation_depannage)),
      affectation_bureau: Boolean(Number(user.affectation_bureau)),
    });
    setError("");
    setMessage("");
  };

  const submit = async (event) => {
    event.preventDefault();
    const done = await run({
      action: form.id ? "update" : "create", id: form.id, nom: form.nom.trim(),
      username: form.username.trim(), password: form.password, role: form.role,
      affectation_depannage: form.affectation_depannage,
      affectation_bureau: form.affectation_bureau,
    });
    if (done) setForm(EMPTY_FORM);
  };

  const remove = async (user) => {
    if (!window.confirm(`Supprimer ${user.nom || user.username} du CRM, des Appels et du Planning ?`)) return;
    const done = await run({ action: "delete", id: Number(user.id) });
    if (done && Number(form.id) === Number(user.id)) setForm(EMPTY_FORM);
  };

  return (
    <main className="users-manager-page">
      <header className="users-manager-header">
        <div className="users-manager-title">
          <span className="um-eyebrow">Administration · AM Services</span>
          <h1>Gestion des utilisateurs</h1>
          <p>Un compte maître synchronisé avec le CRM, les Appels et le Planning.</p>
        </div>
        <div className="um-current-admin"><span>Compte maître connecté</span><strong>{currentUser?.nom || currentUser?.login}</strong></div>
      </header>

      <section className="um-summary" aria-label="Résumé">
        <article><strong>{users.length}</strong><span>comptes CRM</span></article>
        <article><strong>2</strong><span>comptes maîtres</span></article>
        <article><strong>{users.filter((user) => Number(user.synced_appel) && Number(user.synced_planning)).length}</strong><span>comptes synchronisés</span></article>
      </section>

      {message && <div className="um-alert um-alert-success">{message}</div>}
      {error && <div className="um-alert um-alert-error">{error}</div>}

      <div className="um-content-grid">
        <section className="um-panel um-form-panel">
          <div className="um-panel-title">
            <div><span className="um-step">{form.id ? `Compte #${form.id}` : "Nouveau compte"}</span><h2>{form.id ? "Modifier l’utilisateur" : "Ajouter un utilisateur"}</h2></div>
            {form.id && <button className="um-link-button" type="button" onClick={reset}>Annuler</button>}
          </div>

          {editingMaster && <div className="um-master-notice">Compte maître protégé : son identifiant et son rôle ne peuvent pas être changés.</div>}

          <form onSubmit={submit}>
            <div className="um-form-group">
              <label htmlFor="um-name">Nom complet</label>
              <input id="um-name" className="um-input" value={form.nom} onChange={(event) => setForm({ ...form, nom: event.target.value })} placeholder="Ex. Pierre Dupont" required />
            </div>
            <div className="um-form-group">
              <label htmlFor="um-login">Identifiant</label>
              <input id="um-login" className="um-input" value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} placeholder="Ex. pierre.dupont" disabled={editingMaster} required />
              <span className="um-hint">Lettres, chiffres, point, tiret et underscore uniquement.</span>
            </div>
            <div className="um-form-group">
              <label htmlFor="um-password">{form.id ? "Nouveau mot de passe" : "Mot de passe initial"}</label>
              <input id="um-password" className="um-input" type="password" minLength={8} value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder={form.id ? "Vide = mot de passe inchangé" : "8 caractères minimum"} required={!form.id} autoComplete="new-password" />
            </div>
            <div className="um-form-group">
              <label htmlFor="um-role">Rôle</label>
              <select id="um-role" className="um-select" value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} disabled={editingMaster}>
                {ROLES.map((item) => <option key={item.value} value={item.value}>{item.icon} {item.label}</option>)}
              </select>
              <span className="um-role-desc">{selectedRole.desc}</span>
            </div>
            <fieldset className="um-assignment-fieldset">
              <legend>Listes d’affectation des appels</legend>
              <label className="um-check-card">
                <input type="checkbox" checked={form.affectation_depannage} onChange={(event) => setForm({ ...form, affectation_depannage: event.target.checked })} />
                <span><strong>🔧 Dépannage</strong><small>Proposer cet utilisateur dans les appels dépannage.</small></span>
              </label>
              <label className="um-check-card">
                <input type="checkbox" checked={form.affectation_bureau} onChange={(event) => setForm({ ...form, affectation_bureau: event.target.checked })} />
                <span><strong>🏢 Bureau</strong><small>Proposer cet utilisateur dans les appels bureau.</small></span>
              </label>
            </fieldset>
            <button className="um-btn um-btn-primary" type="submit" disabled={saving}>{saving ? "Enregistrement…" : form.id ? "Enregistrer les modifications" : "Créer et synchroniser"}</button>
          </form>
        </section>

        <section className="um-panel um-list-panel">
          <div className="um-toolbar">
            <div><span className="um-step">Annuaire</span><h2>Équipe enregistrée</h2></div>
            <input className="um-input um-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher…" aria-label="Rechercher" />
          </div>

          {loading ? <div className="um-loading">Chargement des comptes…</div> : (
            <div className="um-table-wrap">
              <table className="um-table">
                <thead><tr><th>Utilisateur</th><th>Rôle</th><th>Affectation appels</th><th>Synchronisation</th><th>Dernière connexion</th><th>Actions</th></tr></thead>
                <tbody>
                  {filteredUsers.map((user) => {
                    const userRole = ROLE_MAP[user.role] || ROLES[0];
                    const synced = Number(user.synced_appel) && Number(user.synced_planning);
                    const master = isMaster(user);
                    return (
                      <tr key={user.id}>
                        <td><div className="um-user-cell"><span className="um-avatar">{String(user.nom || user.username).charAt(0).toUpperCase()}</span><span><strong>{user.nom} {master && <em className="um-master-badge">Maître</em>}</strong><small>@{user.username} · #{user.id}</small></span></div></td>
                        <td><span className={`um-role um-role-${user.role}`}>{userRole.icon} {userRole.label}</span></td>
                        <td><div className="um-assignment-tags">
                          {Number(user.affectation_depannage) === 1 && <span className="depannage">🔧 Dépannage</span>}
                          {Number(user.affectation_bureau) === 1 && <span className="bureau">🏢 Bureau</span>}
                          {!Number(user.affectation_depannage) && !Number(user.affectation_bureau) && <span className="none">Aucune</span>}
                        </div></td>
                        <td><div className="um-sync-status"><span className={Number(user.synced_appel) ? "is-ok" : "is-missing"}>Appels</span><span className={Number(user.synced_planning) ? "is-ok" : "is-missing"}>Planning</span></div></td>
                        <td className="um-date">{formatDate(user.last_login)}</td>
                        <td><div className="um-row-actions">
                          {!synced && <button className="um-btn um-btn-sync" type="button" onClick={() => run({ action: "sync", id: Number(user.id) })} disabled={saving}>Synchroniser</button>}
                          <button className="um-btn um-btn-warning" type="button" onClick={() => edit(user)} disabled={saving}>Modifier</button>
                          {!master && <button className="um-btn um-btn-danger" type="button" onClick={() => remove(user)} disabled={saving}>Supprimer</button>}
                        </div></td>
                      </tr>
                    );
                  })}
                  {!filteredUsers.length && <tr><td colSpan="6" className="um-empty">Aucun utilisateur trouvé.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
